import { AnalyticsContext } from '@carvana/eventable-clickstream';
import {
  useEffectWithController,
  useSubsequentEffect
} from '@carvana/merch-react-hooks';
import cx from 'classnames';
import HtmlToReact from 'html-to-react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type React from 'react';
import AutoSuggest, {
  BlurEvent,
  type ChangeEvent,
  SuggestionSelectedEventData
} from 'react-autosuggest';
import SearchBarEvents from '~/constants/SearchBarEvents';
import SearchSource from '~/constants/SearchSource';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import InventoryContext from '~/contexts/InventoryContext';
import SearchEventsDataContext from '~/contexts/SearchEventsDataContext';
import SuggestionsContext from '~/contexts/SuggestionsContext';
import decodeCvnaId from '~/utilities/cvnaId';
import ClearAll from './ClearAll';
import {
  type SuggestionItem,
  type SuggestionSection,
  useSuggestionSections
} from './hooks';
import {
  KeywordSuggestionsWrapper,
  Remove,
  SectionTitle,
  Suggestion
} from './KeywordSuggestions.styles';
import {
  isSavedSuggestion,
  isSavedSearch,
  isSuggestion,
  isButton
} from './utils';

const htmlParser = new HtmlToReact.Parser();

const suggestionTypeMapping = {
  'Saved Searches': 'Saved Search',
  'Your Recent Searches': 'Recent Search',
  Suggestions: 'Suggestion'
};

const dummyFunc = () => {};

type OwnProps = {
  closeModal?: () => void;
  fetchValue: string;
  inputFocus?: boolean;
  inputValue: string;
  isStickySearchbar?: boolean;
  setFetchValue: React.Dispatch<string>;
  setInputFocus: React.Dispatch<boolean>;
  setInputValue: React.Dispatch<string>;
  width?: number;
};

export default function KeywordSuggestions({
  closeModal = dummyFunc,
  fetchValue,
  inputFocus = false,
  inputValue,
  isStickySearchbar = false,
  setFetchValue,
  setInputFocus,
  setInputValue,
  width = 0
}: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const { setAppliedFilters } = useContext(AppliedFiltersContext);
  const { setSearchSource, setKeywordSuggestion } = useContext(
    SearchEventsDataContext
  );

  const {
    addSavedSuggestion,
    clearSavedSuggestions,
    clearSuggestions,
    debouncedFetchSuggestions,
    removeSavedSuggestion,
    suggestions: {
      data: suggestionsData,
      isLoading: isSuggestionsLoading,
      setLoading
    }
  } = useContext(SuggestionsContext);
  const { isLoading } = useContext(InventoryContext).inventory;

  const hasTriggeredNoSearch = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [hasScrollEvent, setHasScrollEvent] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [pressedEnter, setPressedEnter] =
    useState<React.KeyboardEvent<Element> | null>(null);
  const [shouldRenderSuggestions, setShouldRenderSuggestions] = useState(false);
  const [isSuggestionHighlighted, setIsSuggestionHighlighted] = useState(false);
  const [isHoveringSuggestions, setIsHoveringSuggestions] = useState(false);

  const clearAllSavedSuggestions = useCallback(() => {
    trackEvent('Clicked Clear All Saved Suggestions', { isStickySearchbar });
    clearSavedSuggestions();
  }, [clearSavedSuggestions, isStickySearchbar, trackEvent]);

  const sections = useSuggestionSections({
    clearAllSavedSuggestions,
    shouldRenderSuggestions,
    value: fetchValue
  });

  const hasValueChanged = useCallback(
    (val: string) => val === inputValue,
    [inputValue]
  );

  const scrollEventListener = useCallback(() => {
    if (!hasTriggeredNoSearch.current) {
      trackEvent(SearchBarEvents.TypedNoSubmit);
      hasTriggeredNoSearch.current = true;
    }
    document.removeEventListener('scroll', scrollEventListener);

    return () => {};
  }, [fetchValue, isStickySearchbar, trackEvent]);

  useEffect(() => {
    if (
      !inputFocus &&
      fetchValue &&
      !isLoading &&
      !hasTriggeredNoSearch.current
    ) {
      trackEvent(SearchBarEvents.TypedNoSubmit);
      hasTriggeredNoSearch.current = true;

      return () => {};
    }
    if (
      inputFocus &&
      fetchValue &&
      !hasScrollEvent &&
      !hasTriggeredNoSearch.current
    ) {
      document.addEventListener('scroll', scrollEventListener);
      setHasScrollEvent(true);
    }
    if (fetchValue && !hasTriggeredNoSearch.current) {
      const timeout = setTimeout(() => {
        const hasNoChange = hasValueChanged(fetchValue);
        if (hasNoChange && !isLoading) {
          trackEvent(SearchBarEvents.TypedNoSubmit);
          hasTriggeredNoSearch.current = true;
        }
      }, 20000);

      return () => clearTimeout(timeout);
    }
    if (fetchValue.length < 1) {
      hasTriggeredNoSearch.current = false;
      setHasScrollEvent(false);
    }
  }, [
    hasScrollEvent,
    hasValueChanged,
    inputFocus,
    isLoading,
    scrollEventListener,
    trackEvent
  ]);

  const resetValues = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.blur();
      setInputFocus(false);
    }

    if (closeModal) {
      closeModal();
    }

    setFetchValue('');
    clearSuggestions();
    hasTriggeredNoSearch.current = false;
    setHasScrollEvent(false);
  }, [clearSuggestions, closeModal, setFetchValue, setInputFocus]);

  useSubsequentEffect(() => {
    if (!fetchValue && !inputValue && inputRef.current) {
      inputRef.current.focus();
    }
  }, [fetchValue, inputValue, setInputFocus]);

  useEffect(() => {
    if (inputFocus) {
      setFetchValue(inputRef.current?.value || '');
      setShouldRenderSuggestions(inputFocus);
    } else {
      const timeoutId = setTimeout(
        () => setShouldRenderSuggestions(inputFocus),
        500
      );

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [setShouldRenderSuggestions, inputFocus, setFetchValue]);

  useEffectWithController(
    controller => {
      if (fetchValue && inputFocus) {
        setLoading();
        debouncedFetchSuggestions(fetchValue, controller);
      }
    },
    [debouncedFetchSuggestions, fetchValue, inputFocus, setLoading]
  );

  const onSuggestionSelected = useCallback<
    (
      event: React.FormEvent<any>,
      data: SuggestionSelectedEventData<SuggestionItem>
    ) => void
  >(
    (_event, { method, suggestion, suggestionIndex }) => {
      if (!suggestion) {
        return;
      }

      const wasSelected = method === 'click' || method === 'enter';

      // event context
      const eventData = {
        buildKey: isSuggestion(suggestion) ? suggestion?.buildKey : null,
        isStickySearchbar,
        suggestionIndex,
        suggestionsLength: suggestionsData.length,
        suggestionText: !isButton(suggestion) ? suggestion?.text : null,
        textEntered: fetchValue,
        textSource: ''
      };

      // button
      if (isButton(suggestion)) {
        suggestion.onSelect();

        return;
      }

      // saved search logic
      if (isSavedSearch(suggestion)) {
        const { filters = {} } = decodeCvnaId(suggestion.url);
        setAppliedFilters(filters);
        setSearchSource(SearchSource.SavedSearch);
        setInputValue(suggestion.text);

        // ? this could be the cause of no results after a suggestion. If the saved search has no results but is an option
        if (wasSelected) {
          eventData.textSource = SearchSource.SavedSearch;
          setKeywordSuggestion(eventData);
          trackEvent(SearchBarEvents.SuggestionClicked, {
            ...eventData
          });
        }
      }

      // recent search and suggestion logic
      if (isSuggestion(suggestion)) {
        addSavedSuggestion(suggestion);
        setAppliedFilters(suggestion.filters);
        setInputValue(suggestion.text);

        if (suggestion.isSavedSuggestion) {
          setSearchSource(SearchSource.RecentSearch);
        } else {
          setSearchSource(SearchSource.TextSearch);
        }

        if (wasSelected) {
          if (suggestion.isSavedSuggestion) {
            eventData.textSource = SearchSource.RecentSearch;
            setKeywordSuggestion(eventData);
            trackEvent(SearchBarEvents.SuggestionClicked, {
              ...eventData
            });
          } else {
            eventData.textSource = SearchSource.TextSearch;
            setKeywordSuggestion(eventData);
            trackEvent(SearchBarEvents.SuggestionClicked, {
              ...eventData
            });
          }
        }
      }

      resetValues();
    },
    [
      addSavedSuggestion,
      fetchValue,
      isStickySearchbar,
      resetValues,
      setAppliedFilters,
      setInputValue,
      setKeywordSuggestion,
      setSearchSource,
      suggestionsData.length,
      trackEvent
    ]
  );

  useEffect(() => {
    if (isSuggestionsLoading || !pressedEnter) {
      return;
    }

    const section = sections.at(0);
    const suggestion = section?.suggestions.at(0);

    if (section == null || suggestion == null || isButton(suggestion)) {
      return;
    }

    const eventData = {
      buildKey: isSuggestion(suggestion) ? suggestion?.buildKey : null,
      isStickySearchbar,
      result: suggestion
        ? `Selected ${suggestionTypeMapping[section.title]}`
        : `No Results Message Displayed`,
      suggestionIndex: 0,
      suggestionsLength: suggestionsData.length,
      suggestionText: suggestion.text ?? null,
      textEntered: fetchValue
    };

    if (suggestion) {
      setKeywordSuggestion(eventData);
      onSuggestionSelected(pressedEnter, {
        method: 'enter',
        sectionIndex: null,
        suggestion,
        suggestionIndex: 0,
        suggestionValue: suggestion.text
      });
    }

    setPressedEnter(null);
    trackEvent(SearchBarEvents.PressedEnter, {
      ...eventData,
      textSource: SearchSource.TextSearch
    });
  }, [
    fetchValue,
    isStickySearchbar,
    isSuggestionsLoading,
    onSuggestionSelected,
    pressedEnter,
    sections,
    setKeywordSuggestion,
    suggestionsData.length,
    trackEvent
  ]);

  const SuggestionWrapper = useMemo(
    () => (suggestion: SuggestionItem) => {
      const firstSuggestion =
        sections.length > 0 ? sections[0].suggestions[0] : null;
      const isFirstSuggestion = suggestion === firstSuggestion;

      const firstSuggestionClassName = `${
        isFirstSuggestion &&
        !isHoveringSuggestions &&
        !isSuggestionHighlighted &&
        inputValue.length > 0
          ? 'bg-grey-1 rounded-md'
          : ''
      }`;

      if (isButton(suggestion)) {
        return (
          <Suggestion
            data-qa="suggestion-wrapper"
            className={firstSuggestionClassName}
          >
            {suggestion.element}
          </Suggestion>
        );
      }

      const textToShow = suggestion.highlightedText
        ? htmlParser.parse(suggestion.highlightedText)
        : suggestion.text;

      return (
        <Suggestion
          data-qa="suggestion-wrapper"
          className={firstSuggestionClassName}
        >
          <span className="flex">
            <span>{textToShow}</span>
          </span>
          {isSavedSuggestion(suggestion) ? (
            <Remove
              data-analytics-id={SearchBarEvents.RemovedRecentSearch}
              onClick={e => {
                e.stopPropagation();
                removeSavedSuggestion(suggestion);
              }}
            />
          ) : null}
        </Suggestion>
      );
    },
    [
      removeSavedSuggestion,
      sections,
      isSuggestionHighlighted,
      isHoveringSuggestions,
      inputValue.length
    ]
  );

  const SectionTitleWrapper = useMemo(
    () => (section: SuggestionSection) =>
      section.title ? (
        <div className="flex items-center justify-between">
          <SectionTitle className={section.title ? '' : 'no-top-margin'}>
            {section.titleIcon}
            {section.title}
          </SectionTitle>
          {section.clearable && (
            <ClearAll className="m:hidden" onClick={clearAllSavedSuggestions} />
          )}
        </div>
      ) : null,
    [clearAllSavedSuggestions]
  );

  const SuggestionsContainer = useMemo(
    () =>
      ({ containerProps, children }) => {
        const showNoSuggestionResults =
          suggestionsData.length === 0 &&
          fetchValue &&
          !isSuggestionsLoading &&
          inputFocus;

        return (
          <div
            {...containerProps}
            className={cx(
              containerProps.className,
              '!bg-transparent !p-0 drop-shadow overflow-hidden'
            )}
          >
            <div
              className={cx(
                'bg-white duration-500 overflow-hidden px-16 transition-all',
                { '-translate-y-full': !inputFocus }
              )}
              onMouseEnter={() => setIsHoveringSuggestions(true)}
              onMouseLeave={() => setIsHoveringSuggestions(false)}
            >
              {showNoSuggestionResults ? (
                <div className="p-16 text-grey-5">
                  No results for &quot;{fetchValue}&quot;
                </div>
              ) : (
                children
              )}
            </div>
          </div>
        );
      },
    [fetchValue, inputFocus, isSuggestionsLoading, suggestionsData.length]
  );

  const onChange = useCallback<
    (event: React.FormEvent<HTMLElement>, params: ChangeEvent) => void
  >(
    (_event, { newValue, method }) => {
      if (method === 'type') {
        setIsTyping(true);
        setFetchValue(newValue);
      } else {
        setIsTyping(false);
      }
      setInputValue(newValue);
    },
    [setFetchValue, setInputValue]
  );

  const onFocus = useCallback<React.FocusEventHandler>(
    e => {
      e.stopPropagation();
      setInputFocus(true);
    },
    [setInputFocus]
  );

  const onBlur = useCallback<
    (
      event: React.FocusEvent<HTMLElement>,
      params?: BlurEvent<SuggestionItem>
    ) => void
  >(() => {
    setInputFocus(false);
  }, [setInputFocus]);

  const onKeyDown = useCallback<React.KeyboardEventHandler>(
    event => {
      const shouldQueueEnterPress =
        event.key === 'Enter' && isTyping && fetchValue.length > 0;

      if (shouldQueueEnterPress) {
        setPressedEnter(event);
      }

      if (event.key === 'Escape') {
        resetValues();
      }
    },
    [fetchValue.length, isTyping, resetValues]
  );

  const handleSuggestionHighlighted = ({ suggestion }) => {
    setIsSuggestionHighlighted(Boolean(suggestion));
  };

  return (
    <KeywordSuggestionsWrapper
      width={width}
      isStickySearchbar={isStickySearchbar}
    >
      <AutoSuggest<SuggestionItem, SuggestionSection>
        alwaysRenderSuggestions
        focusInputOnSuggestionClick={false}
        getSectionSuggestions={section => section.suggestions}
        getSuggestionValue={suggestion =>
          isButton(suggestion) ? inputValue : suggestion.text
        }
        inputProps={{
          // @ts-ignore
          'data-qa': 'keyword-search-input',
          autoFocus: false,
          onBlur,
          onChange,
          onFocus,
          onKeyDown,
          placeholder: 'Search makes, models or any keywords',
          ref: inputRef,
          value: inputValue
        }}
        multiSection
        renderSuggestion={SuggestionWrapper}
        renderSectionTitle={SectionTitleWrapper}
        renderSuggestionsContainer={SuggestionsContainer}
        suggestions={sections}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsFetchRequested={dummyFunc}
        onSuggestionHighlighted={handleSuggestionHighlighted}
      />
    </KeywordSuggestionsWrapper>
  );
}
